import React, { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router';
import Icon from '../components/Icon';
import ThemeToggle from '../components/ThemeToggle';
import './LandingPage.css';

const FEATURES = [
  'Multi-currency balances',
  'Asset allocation targets',
  'Link refunds to purchases',
];

const LandingPage: React.FC = () => {
  const { loginWithRedirect, isAuthenticated } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async () => {
    await loginWithRedirect({
      appState: { returnTo: '/dashboard' },
    });
  };

  return (
    <div className="LandingPage">
      <header className="LandingPage-header">
        <div className="brand">
          <img
            src={`${process.env.PUBLIC_URL}/logo192.png`}
            alt="Kelvin's Finance App"
            className="brand-mark"
          />
        </div>
        <div className="LandingPage-header-actions">
          <ThemeToggle />
          <button className="btn" onClick={handleLogin}>
            Log in
          </button>
        </div>
      </header>

      <main className="LandingPage-main">
        <div className="LandingPage-copy">
          <span className="LandingPage-eyebrow">
            Connected to your NZ banks via Akahu
          </span>
          <h1>Take control of your finances with ease.</h1>
          <p className="LandingPage-lede">
            See every account in one place, track spending by category, and keep
            your investments on target across currencies.
          </p>
          <div className="LandingPage-actions">
            <button
              className="btn btn-primary LandingPage-button"
              onClick={handleLogin}
            >
              Log in to your dashboard
              <Icon name="arrowRight" size={18} strokeWidth={2.4} />
            </button>
            <span className="LandingPage-secure">
              <Icon name="lock" size={16} />
              Secure sign-in with Auth0
            </span>
          </div>
          <ul className="LandingPage-features">
            {FEATURES.map((feature) => (
              <li key={feature}>
                <Icon name="check" size={16} strokeWidth={2.4} />
                {feature}
              </li>
            ))}
          </ul>
        </div>

        {/* Decorative product preview */}
        <div className="LandingPage-preview" aria-hidden="true">
          <div className="preview-card">
            <div className="preview-label">Total balance · NZD</div>
            <div className="preview-skeleton preview-skeleton-lg" />
            <div className="preview-skeleton preview-skeleton-sm" />
          </div>
          <div className="preview-row">
            <div className="preview-card">
              <div className="preview-label">USD</div>
              <div className="preview-skeleton" />
            </div>
            <div className="preview-card">
              <div className="preview-label">AUD</div>
              <div className="preview-skeleton" />
            </div>
          </div>
          <div className="preview-card preview-allocation">
            <div className="preview-title">Asset allocation</div>
            {[
              { target: 88, actual: 91, over: true },
              { target: 63, actual: 66, over: true },
              { target: 38, actual: 26, over: false },
            ].map((bar, index) => (
              <div className="preview-bar" key={index}>
                <div
                  className="preview-bar-target"
                  style={{ width: `${bar.target}%` }}
                />
                <div
                  className={`preview-bar-actual${bar.over ? '' : ' is-under'}`}
                  style={{ width: `${bar.actual}%` }}
                />
              </div>
            ))}
          </div>
        </div>
      </main>

      <footer className="LandingPage-footer">
        Read-only access to your accounts through Akahu.
      </footer>
    </div>
  );
};

export default LandingPage;
